import { motion, AnimatePresence } from 'framer-motion'
import { Shoot } from '../types'
import { 
  ExclamationTriangleIcon,
  XMarkIcon,
  ClockIcon,
  UserIcon,
  CameraIcon,
  VideoCameraIcon
} from '@heroicons/react/24/outline'

interface DoubleBookingModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  conflictingShoots: Shoot[]
  newShootData: {
    title: string
    date: string
    start_time: string
    end_time: string
    client?: { name: string }
  }
}

const DoubleBookingModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  conflictingShoots, 
  newShootData 
}: DoubleBookingModalProps) => {
  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(':')
    const hour = parseInt(hours)
    const ampm = hour >= 12 ? 'PM' : 'AM'
    const displayHour = hour % 12 || 12
    return `${displayHour}:${minutes} ${ampm}`
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString + 'T00:00:00').toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  if (!isOpen) return null

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        />
        
        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          className="relative w-full max-w-2xl glass-mockup rounded-2xl p-6 max-h-[90vh] overflow-y-auto"
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center">
                <ExclamationTriangleIcon className="w-6 h-6 text-amber-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Double Booking Warning</h2>
                <p className="text-gray-400 text-sm">Time conflict detected</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          {/* Warning Message */}
          <div className="mb-6 p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl">
            <p className="text-amber-300 font-medium mb-2">
              The time slot you selected conflicts with {conflictingShoots.length} existing shoot{conflictingShoots.length > 1 ? 's' : ''}:
            </p>
            <p className="text-gray-300 text-sm">
              Please review the conflicts below and confirm if you want to proceed with the double booking.
            </p>
          </div>

          {/* New Shoot Details */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-white mb-3">New Shoot Details</h3>
            <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl">
              <div className="flex items-center space-x-3 mb-3">
                <CameraIcon className="w-5 h-5 text-blue-400" />
                <h4 className="text-white font-medium">{newShootData.title}</h4>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                {newShootData.client && (
                  <div className="flex items-center space-x-2 text-gray-300">
                    <UserIcon className="w-4 h-4" />
                    <span>{newShootData.client.name}</span>
                  </div>
                )}
                <div className="flex items-center space-x-2 text-gray-300">
                  <ClockIcon className="w-4 h-4" />
                  <span>{formatTime(newShootData.start_time)} - {formatTime(newShootData.end_time)}</span>
                </div>
              </div>
              <p className="text-gray-400 text-sm mt-2">
                {formatDate(newShootData.date)}
              </p>
            </div>
          </div>

          {/* Conflicting Shoots */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-white mb-3">Conflicting Shoots</h3>
            <div className="space-y-3">
              {conflictingShoots.map((shoot) => (
                <div key={shoot.id} className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
                  <div className="flex items-center space-x-3 mb-3">
                    {shoot.type === 'photo' ? (
                      <CameraIcon className="w-5 h-5 text-red-400" />
                    ) : (
                      <VideoCameraIcon className="w-5 h-5 text-red-400" />
                    )}
                    <h4 className="text-white font-medium">{shoot.title}</h4>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    <div className="flex items-center space-x-2 text-gray-300">
                      <UserIcon className="w-4 h-4" />
                      <span>{shoot.client?.name || 'No client'}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-gray-300">
                      <ClockIcon className="w-4 h-4" />
                      <span>{formatTime(shoot.start_time)} - {formatTime(shoot.end_time)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={onClose}
              className="flex-1 px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-xl font-medium transition-colors"
            >
              Cancel & Review Times
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={onConfirm}
              className="flex-1 px-6 py-3 bg-amber-500 hover:bg-amber-600 text-white rounded-xl font-medium transition-colors"
            >
              Confirm Double Booking
            </motion.button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  )
}

export default DoubleBookingModal 